import React, { useEffect, useMemo, useState } from 'react';
import './App.css';

// Import components
import DocumentUploader from './components/DocumentUploader';
import ChatInterface from './components/ChatInterface';
import LoadingModal from './components/LoadingModal';

// Import custom hooks
import useDocuments from './hooks/useDocuments';
import useChatWithAI from './hooks/useChatWithAI';

const guidBootstrapRequests = new Map();

const getGuidBootstrapRequestUrl = (endpoint, code, guid) => {
  const query = new URLSearchParams({ code, guid });
  const separator = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${separator}${query.toString()}`;
};

const requestGuidBootstrapData = (guid) => {
  if (guidBootstrapRequests.has(guid)) {
    return guidBootstrapRequests.get(guid);
  }

  const endpoint = process.env.REACT_APP_AZURE_FUNCTIONS_API_GETIRBPROTOCOL_ENDPOINT;
  const code = process.env.REACT_APP_AZURE_FUNCTIONS_API_GETIRBPROTOCOL_KEY;

  if (!endpoint || !code) {
    return Promise.reject(
      new Error('GUID preload is not configured. Missing endpoint or function key.')
    );
  }

  const requestUrl = getGuidBootstrapRequestUrl(endpoint, code, guid);
  const requestPromise = fetch(requestUrl, { method: 'GET' })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`Failed to load guid data (status ${response.status}).`);
      }

      const payload = await response.json();
      const protocol = typeof payload?.protocol === 'string' ? payload.protocol.trim() : '';
      const report = typeof payload?.report === 'string' ? payload.report.trim() : '';

      if (!protocol || !report) {
        throw new Error('GUID response is missing required protocol/report fields.');
      }

      return { protocol, report, sourceGuid: guid };
    })
    .catch((error) => {
      guidBootstrapRequests.delete(guid);
      throw error;
    });

  guidBootstrapRequests.set(guid, requestPromise);
  return requestPromise;
};

function App() {
  // Maximum token limit for API calls
  const MAX_TOKENS = 100000;
  const guidParam = useMemo(() => {
    const rawGuid = new URLSearchParams(window.location.search).get('guid');
    return rawGuid ? rawGuid.trim() : '';
  }, []);
  const [isGuidLoading, setIsGuidLoading] = useState(false);
  const [guidLoadError, setGuidLoadError] = useState('');
  
  // Document management: uploads, list, and processing state
  const { 
    documents, 
    isUploading, 
    handleFiles, 
    removeDocument,
    addProtocolDocument
  } = useDocuments();
  
  // Chat functionality with AI integration
  const {
    chatMessages,
    currentMessage,
    setCurrentMessage,
    isWaitingForResponse,
    isStreaming,
    streamingResponse,
    handleSubmit,
    analyzeDocuments,
    isAnalyzed,
    hydrateInitialReport
  } = useChatWithAI(documents, null, MAX_TOKENS);

  useEffect(() => {
    if (!guidParam) return undefined;

    let isActive = true;
    setGuidLoadError('');
    setIsGuidLoading(true);

    requestGuidBootstrapData(guidParam)
      .then(({ protocol, report, sourceGuid }) => {
        if (!isActive) return;
        addProtocolDocument(protocol, sourceGuid);
        hydrateInitialReport(report);
      })
      .catch((error) => {
        console.error('Error preloading protocol/report from guid:', error);
        if (!isActive) return;
        setGuidLoadError(
          'We could not preload the protocol from this link. You can still upload documents and continue.'
        );
      })
      .finally(() => {
        if (isActive) {
          setIsGuidLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, [guidParam, addProtocolDocument, hydrateInitialReport]);

  return (
      <div className="App">
        <header className="App-header">
          <h1>IRB Document Review</h1>
        </header>
        <LoadingModal
          isOpen={isGuidLoading}
          message="Loading IRB protocol and report..."
        />

      <main className={isAnalyzed ? "app-main" : "app-landing"}>
        {!isAnalyzed ? (
          <div className="landing-content">
            {guidLoadError && (
              <div className="guid-error-banner" role="alert">
                {guidLoadError}
              </div>
            )}
            <DocumentUploader
              documents={documents}
              handleFiles={handleFiles}
              removeDocument={removeDocument}
              isUploading={isUploading}
            />
            <div className="landing-analyze">
              <button
                className="analyze-button"
                onClick={analyzeDocuments}
                disabled={documents.length === 0 || isWaitingForResponse}
              >
                Analyze Documents
              </button>
              <p className="analyze-hint">
                {documents.length === 0
                  ? "Upload documents first to enable analysis"
                  : "Click to analyze your uploaded documents"}
              </p>
            </div>
          </div>
        ) : (
          <ChatInterface
            chatMessages={chatMessages}
            currentMessage={currentMessage}
            setCurrentMessage={setCurrentMessage}
            handleSubmit={handleSubmit}
            isWaitingForResponse={isWaitingForResponse}
            isStreaming={isStreaming}
            streamingResponse={streamingResponse}
            documents={documents}
            handleFiles={handleFiles}
            removeDocument={removeDocument}
            analyzeDocuments={analyzeDocuments}
            isAnalyzed={isAnalyzed}
          />
        )}
      </main>

      <footer>
        <p>AI can make mistakes. Please contact IRB if you are unsure about the advice it gives you.</p>
      </footer>
    </div>
  );
}

export default App;
